import { Alert, Button, Card, Descriptions, Skeleton, Space, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canWriteMemberships } from '@/layouts/navigation';
import { useMembership } from '../hooks/useMembership';
import { BillingCycleBadge, MembershipStatusBadge } from '../components/MembershipBadges';
import { formatCop, formatDiscountPct } from '../utils/format';

/** Detalle de solo lectura de una Membresía (`/membresias/:membershipId`). */
export function MembershipDetailPage() {
  const { membershipId } = useParams<{ membershipId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { membership, loading, error, notFound, forbidden, refresh } =
    useMembership(membershipId);
  const canWrite = canWriteMemberships(user?.role);

  const goToList = () => navigate('/membresias');

  return (
    <div className="membresias-page">
      <div className="membresias-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de membresías"
            onClick={goToList}
          >
            Volver
          </Button>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {membership?.name ?? 'Detalle de la membresía'}
          </Typography.Title>
        </Space>
        {canWrite && membership && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() =>
              navigate(`/membresias/${encodeURIComponent(membership.id)}/editar`)
            }
          >
            Editar membresía
          </Button>
        )}
      </div>

      {loading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      ) : notFound ? (
        <Alert
          type="warning"
          showIcon
          message="No encontramos la membresía"
          description="Puede que se haya eliminado o que el enlace esté incompleto."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : forbidden ? (
        <Alert
          type="warning"
          showIcon
          message="Sin acceso a esta membresía"
          description="No tienes permisos para consultar esta membresía inactiva. Inicia sesión con un rol administrativo."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la membresía"
          description={error}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : membership ? (
        <Card>
          <Descriptions column={2} size="middle">
            <Descriptions.Item label="Identificador">
              <Typography.Text style={{ fontFamily: 'var(--ge-font-mono)' }}>
                {membership.id}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              <MembershipStatusBadge active={membership.active} />
            </Descriptions.Item>
            <Descriptions.Item label="Ciclo de facturación">
              <BillingCycleBadge cycle={membership.billingCycle} />
            </Descriptions.Item>
            <Descriptions.Item label="Precio">
              {formatCop(membership.priceCop, membership.currency || 'COP')}
            </Descriptions.Item>
            <Descriptions.Item label="Moneda">
              {membership.currency || 'COP'}
            </Descriptions.Item>
            <Descriptions.Item label="Descuento de tarifa">
              {formatDiscountPct(membership.tariffDiscountPct)}
            </Descriptions.Item>
            <Descriptions.Item label="Descripción" span={2}>
              {membership.description?.trim() || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Beneficios" span={2}>
              {membership.benefits && membership.benefits.length > 0 ? (
                <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                  {membership.benefits.map((benefit, index) => (
                    <li key={`${index}-${benefit}`}>{benefit}</li>
                  ))}
                </ul>
              ) : (
                <Typography.Text type="secondary">Sin beneficios</Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ) : null}
    </div>
  );
}
